// The display-language preference on a client (doc/20261023-i18n-zh-cn.md §3.2): the device record, the pending
// choice, choice-time stamps on the server's clock, and one save in flight at a time. Framework-free: webui, the
// panes and the control console each give it their storage, their save request and their way to show a language.

import { isSupportedDisplayLanguage, resolveDisplayLanguage } from './locale.mjs';

export const STORAGE_KEY = 'lexside.language';

function parseRecord(text) {
  try {
    const value = JSON.parse(text);
    if (value === null || typeof value !== 'object' || typeof value.id !== 'string') return null;
    return {
      id: value.id,
      userId: typeof value.userId === 'string' ? value.userId : null,
      pending: value.pending === true && typeof value.userId === 'string' && Number.isSafeInteger(value.chosenAt),
      chosenAt: Number.isSafeInteger(value.chosenAt) ? value.chosenAt : null,
    };
  } catch {
    return null;
  }
}

/**
 * @param {object} options
 * @param {{ read(): string | null, write(text: string): void, remove(): void }} options.storage per-origin storage;
 *   failures are the storage's own to swallow
 * @param {(language: string, chosenAt: number) => Promise<{ applied: boolean, displayLanguage: string | null,
 *   chosenAt: number }>} options.save the signed-in person's save request
 * @param {(language: string) => Promise<void>} options.show loads and shows a language; rejects when it cannot
 * @param {() => number} [options.now]
 */
export function createDisplayLanguagePreference({ storage, save, show, now = Date.now }) {
  let offset = 0;
  let userId = null;
  /** Bumped whenever the signed-in person may have changed: work from an earlier generation is dropped. */
  let generation = 0;
  let current = null;
  let inFlight = false;
  let queued = null;
  /** The newest choice: only its outcome may change what is shown. */
  let latest = null;
  /** Choices whose save has been answered. */
  const answered = new WeakSet();
  /** Choices being sent, or queued to be: the only unanswered choices a refresh leaves alone. */
  const inflight = new WeakSet();
  /** The newest preference the server has confirmed on this page: an older session snapshot never undoes it. */
  let acknowledged = null;
  /** The last stamp issued or seen: new choices stamp strictly later, whatever recalibration does to the offset. */
  let lastStamp = 0;

  const read = () => {
    const text = storage.read();
    return text === null ? null : parseRecord(text);
  };
  const write = (record) => storage.write(JSON.stringify(record));
  const live = (choice) => choice.generation === generation && choice.userId === userId;

  // Shows run one after another and always end on the newest request, however long each language takes to load.
  let target = null;
  let showing = Promise.resolve();
  const display = (language) => {
    target = language;
    showing = showing.then(async () => {
      while (target !== null && target !== current) {
        const next = target;
        try {
          await show(next);
          current = next;
        } catch {
          // A language that failed to load leaves the current one in place.
          if (target === next) target = current;
        }
      }
    });
    return showing;
  };

  /** Server time, from the clock offset measured at the last session or discovery answer. */
  const serverNow = () => Math.round(now() + offset);
  /** A new choice's stamp: server time, but never at or before a stamp this page has already issued or seen. */
  const stamp = () => {
    lastStamp = Math.max(serverNow(), lastStamp + 1);
    return lastStamp;
  };
  const sawStamp = (value) => {
    if (Number.isSafeInteger(value)) lastStamp = Math.max(lastStamp, value);
  };

  async function send(choice) {
    inflight.add(choice);
    if (inFlight) {
      if (queued !== null) inflight.delete(queued);
      queued = choice;
      return;
    }
    inFlight = true;
    try {
      let next = choice;
      while (next !== null) {
        queued = null;
        // A choice made before the person changed is never sent under the new person's session.
        if (!live(next)) {
          inflight.delete(next);
          next = queued;
          continue;
        }
        let answer = null;
        try {
          answer = await save(next.language, next.chosenAt);
        } catch {
          // Offline, expired session: the pending record stays, and the next load or sign-in saves it.
        }
        inflight.delete(next);
        if (answer !== null) answered.add(next);
        if (answer !== null && live(next)) {
          sawStamp(answer.chosenAt);
          if (answer.displayLanguage !== null && (acknowledged === null || acknowledged.userId !== next.userId
            || answer.chosenAt >= acknowledged.chosenAt)) {
            acknowledged = { userId: next.userId, language: answer.displayLanguage, chosenAt: answer.chosenAt };
          }
          // Settle against the newest confirmed preference, which a session snapshot may have made newer than this
          // answer: a delayed answer never undoes it.
          const best = acknowledged !== null && acknowledged.userId === next.userId ? acknowledged
            : { language: answer.displayLanguage ?? next.language, chosenAt: answer.chosenAt };
          const record = read();
          if (record !== null && record.pending && record.userId === next.userId && record.chosenAt === next.chosenAt) {
            write({ id: best.language, userId: next.userId, pending: false, chosenAt: best.chosenAt });
          }
          // Applied or not, the latest choice settles on the newest confirmed value, unless a newer choice has been made
          // since.
          if (latest === next && queued === null) await display(best.language);
        }
        next = queued;
      }
    } finally {
      inFlight = false;
    }
  }

  /** A new signed-in context: drop everything queued for the previous one. */
  function reset(nextUser) {
    generation += 1;
    userId = nextUser;
    queued = null;
    latest = null;
  }

  return {
    /** Measures the offset between this clock and the server's from an answer carrying `serverNow`. */
    calibrate(serverTime, sentAt, receivedAt) {
      if (Number.isFinite(serverTime)) offset = serverTime - (sentAt + receivedAt) / 2;
    },

    serverNow,

    /** The language to show before anyone is signed in: device choice → zone default → browser → English. */
    signedOut({ zoneDefault = null, browser = [] } = {}) {
      if (userId !== null) reset(null);
      const record = read();
      if (record !== null) sawStamp(record.chosenAt);
      return resolveDisplayLanguage([record?.id ?? null, zoneDefault, browser]);
    },

    /** Shows what `signedOut` resolves. */
    async showSignedOut(context) {
      await display(this.signedOut(context));
    },

    /**
     * Reconciles the signed-in person's stored preference with this device and shows the result (§3.2): a pending
     * choice for the same person wins and is re-sent with its own stamp; a person who never chose saves what they
     * see now.
     */
    async signedIn({ user: snapshot, zoneDefault = null, browser = [] }) {
      sawStamp(snapshot.displayLanguageChosenAt);
      // A preference confirmed on this page is newer than a session snapshot taken before it.
      const confirmed = acknowledged !== null && acknowledged.userId === snapshot.id
        && acknowledged.chosenAt > (snapshot.displayLanguageChosenAt ?? 0);
      const user = confirmed ? { ...snapshot, displayLanguage: acknowledged.language, displayLanguageChosenAt: acknowledged.chosenAt } : snapshot;
      // A snapshot newer than anything confirmed here becomes the confirmed preference: a later, staler one can't undo it.
      if (!confirmed && snapshot.displayLanguage !== null && Number.isSafeInteger(snapshot.displayLanguageChosenAt)
        && (acknowledged === null || acknowledged.userId !== snapshot.id || snapshot.displayLanguageChosenAt > acknowledged.chosenAt)) {
        acknowledged = { userId: snapshot.id, language: snapshot.displayLanguage, chosenAt: snapshot.displayLanguageChosenAt };
      }
      const sameUser = user.id === userId;
      if (sameUser && latest !== null && latest.userId === user.id && !answered.has(latest)) {
        if (inflight.has(latest)) {
          // The same person, with a choice still being sent from this page: it is newer than this snapshot.
          await display(latest.language);
          return;
        }
        // A choice not yet saved (its save failed, or its language had not loaded): once its language shows, send it
        // with its own stamp, even if storage could not keep it. Until then it stays retryable.
        const retry = latest;
        await display(retry.language);
        if (latest === retry && live(retry) && current === retry.language) void send(retry);
        return;
      }
      if (!sameUser) reset(user.id);
      const record = read();
      if (record !== null && record.userId === user.id) sawStamp(record.chosenAt);
      // A pending record names a language this build ships, or it is ignored (it may come from a newer build).
      if (record !== null && record.pending && record.userId === user.id && isSupportedDisplayLanguage(record.id)) {
        const choice = { language: record.id, chosenAt: record.chosenAt, userId: user.id, generation };
        latest = choice;
        await display(resolveDisplayLanguage([record.id, user.displayLanguage, zoneDefault, browser]));
        // A replayed intent is saved only once its language actually shows; otherwise it stays pending and retryable.
        if (latest === choice && live(choice) && current === choice.language) void send(choice);
        return;
      }
      const deviceChoice = record?.id ?? null;
      if (user.displayLanguage !== null) {
        await display(resolveDisplayLanguage([user.displayLanguage, deviceChoice, zoneDefault, browser]));
        return;
      }
      const language = resolveDisplayLanguage([deviceChoice, zoneDefault, browser]);
      const choice = { language, chosenAt: stamp(), userId: user.id, generation };
      write({ id: language, userId: user.id, pending: true, chosenAt: choice.chosenAt });
      latest = choice;
      await display(language);
      if (latest === choice && live(choice) && current === language) void send(choice);
    },

    /**
     * A person's choice from the switch: stamped and recorded at the moment of the click, shown, then saved if someone
     * is signed in. Rejects, keeping the previous preference, when the language cannot be shown.
     */
    async choose(language) {
      const choice = { language, chosenAt: stamp(), userId, generation };
      const previous = storage.read();
      const previousChoice = latest;
      const intent = JSON.stringify(userId === null
        ? { id: language, userId: null, pending: false, chosenAt: null }
        : { id: language, userId, pending: true, chosenAt: choice.chosenAt });
      storage.write(intent);
      latest = choice;
      await display(language);
      if (latest !== choice || !live(choice)) return; // a newer choice, or another person, took over
      if (current !== language) {
        answered.add(choice);
        // The earlier choice, saved or not, is the latest again, with its own stamp and retry eligibility.
        latest = previousChoice;
        // Undo only this choice's own record: another tab may have written a newer one meanwhile.
        if (storage.read() === intent) {
          if (previous === null) storage.remove();
          else storage.write(previous);
        }
        throw new Error('display_language_unavailable');
      }
      if (choice.userId !== null) await send(choice);
    },

    language: () => current,
  };
}
